/**
 * Authentication related constants
 * Uses OpenAPI generated types for type-safe endpoints
 */

import type { paths } from "../types/api";

// Extract auth endpoint paths from OpenAPI schema
export type AuthEndpointPath = Extract<
  keyof paths,
  | "/api/auth/login"
  | "/api/auth/resend-otp"
  | "/api/auth/verify-otp"
  | "/api/auth/register"
  | "/api/auth/refresh"
  | "/api/auth/verify-email"
  | "/api/auth/logout"
  | "/api/auth/me"
  | "/api/auth/forgot-password"
  | "/api/auth/reset-password"
  | "/api/auth/resend-verification"
>;

// Storage keys
export const AUTH_TOKEN_KEY = "accessToken";
export const USER_KEY = "user_data";

// API endpoints (from OpenAPI schema)
export const AUTH_ENDPOINTS = {
  LOGIN: "/api/auth/login",
  RESEND_OTP: "/api/auth/resend-otp",
  VERIFY_OTP: "/api/auth/verify-otp",
  /** Disabled on the server unless AUTH_SELF_REGISTRATION_ENABLED=true; not used by the dashboard UI. */
  REGISTER: "/api/auth/register",
  LOGOUT: "/api/auth/logout",
  REFRESH: "/api/auth/refresh",
  FORGOT_PASSWORD: "/api/auth/forgot-password",
  RESET_PASSWORD: "/api/auth/reset-password",
  VERIFY_EMAIL: "/api/auth/verify-email",
  RESEND_VERIFICATION: "/api/auth/resend-verification",
  ME: "/api/auth/me",
  UPDATE_ME: "/api/auth/me",
  CHANGE_PASSWORD: "/api/auth/change-password",
  COMPLETE_INITIAL_PASSWORD: "/api/auth/complete-initial-password",
} as const satisfies Record<string, AuthEndpointPath | string>;

/**
 * CMS → dashboard SSO handoff. Custom route (not in the generated OpenAPI spec),
 * so it lives outside the schema-constrained AUTH_ENDPOINTS map above.
 */
export const SSO_EXCHANGE_ENDPOINT = "/api/auth/sso-exchange";

/** Dashboard → CMS SSO handoff (custom route, not in OpenAPI). */
export const ISSUE_CMS_HANDOFF_ENDPOINT = "/api/auth/issue-cms-handoff";

/** Dashboard → ticketing SSO handoff (custom route, not in OpenAPI). */
export const ISSUE_TICKETING_HANDOFF_ENDPOINT = "/api/auth/issue-ticketing-handoff";

/**
 * Auth endpoints that should NOT trigger automatic token refresh on 401
 * These are public endpoints or token-related endpoints
 */
export const AUTH_NO_RETRY_ENDPOINTS: readonly string[] = [
  AUTH_ENDPOINTS.LOGIN,
  AUTH_ENDPOINTS.RESEND_OTP,
  AUTH_ENDPOINTS.VERIFY_OTP,
  AUTH_ENDPOINTS.REGISTER,
  AUTH_ENDPOINTS.REFRESH,
  AUTH_ENDPOINTS.VERIFY_EMAIL,
  AUTH_ENDPOINTS.FORGOT_PASSWORD,
  AUTH_ENDPOINTS.RESET_PASSWORD,
  SSO_EXCHANGE_ENDPOINT,
  ISSUE_CMS_HANDOFF_ENDPOINT,
  ISSUE_TICKETING_HANDOFF_ENDPOINT,
] as const;

/** Dispatched by `http-client` after a successful silent refresh so Zustand stays in sync with cookies. */
export const ACCESS_TOKEN_REFRESHED_EVENT =
  "simple-arab-admin:access-token-refreshed";

// User status
export const USER_STATUS = {
  ACTIVE: "active",
  PENDING: "pending",
  SUSPENDED: "suspended",
  DELETED: "deleted",
} as const;

// Token expiry
export const TOKEN_EXPIRY = {
  ACCESS_TOKEN: 15 * 60 * 1000, // 15 minutes
  REFRESH_TOKEN: 7 * 24 * 60 * 60 * 1000, // 7 days
  REMEMBER_ME: 30 * 24 * 60 * 60 * 1000, // 30 days
} as const;

export const AUTH_UI_MESSAGES = {
  OTP_SENT: "تم إرسال رمز التحقق إلى بريدك الإلكتروني",
} as const;

const AUTH_API_MESSAGE_MAP: Record<string, string> = {
  "otp sent to your email": AUTH_UI_MESSAGES.OTP_SENT,
  "login session expired. please sign in again.": "انتهت جلسة تسجيل الدخول. يرجى تسجيل الدخول مرة أخرى.",
};

export function translateAuthMessage(
  message: string | null | undefined,
): string | null {
  if (!message?.trim()) return null;

  const normalized = message.trim().toLowerCase();
  return AUTH_API_MESSAGE_MAP[normalized] ?? message;
}
